import {
  FileText,
  ScanLine,
  Receipt,
  CheckCircle2,
  Wallet,
  Settings,
  Calculator,
  MessagesSquare,
  PhoneIncoming,
  TrendingUp,
  UserRound,
  Users,
  Megaphone,
  Target,
  BarChart3,
  Inbox,
} from 'lucide-react';

// The product's main navigation, in sidebar order. Shared so the onboarding
// tour spotlights the very same entries the sidebar renders (matched by
// `data-tour`) and shows their real icons - one list, no drift.
//
// `group` is optional - items sharing a group value get rendered inside a
// collapsible section headed by that group's label, which auto-expands
// when the current route belongs to it (see Layout.js's NavGroup).
export const NAV_ITEMS = [
  { path: '/',             tourKey: 'dashboard',   labelKey: 'nav.dashboard',   icon: FileText },
  { path: '/scans',        tourKey: 'scans',       labelKey: 'nav.scans',       icon: ScanLine },
  { path: '/calculator',   tourKey: 'calculator',  labelKey: 'nav.calculator',  icon: Calculator,   badge: 'free' },
  { path: '/qa-validator', tourKey: 'qaValidator', labelKey: 'nav.qaValidator', icon: CheckCircle2, badge: 'free' },
  { path: '/usage',        tourKey: 'usage',       labelKey: 'nav.usage',       icon: Wallet },
  { path: '/settings',     tourKey: 'settings',    labelKey: 'nav.settings',    icon: Settings },
  // Everything below sits inside the Others dropdown at the very bottom, so
  // auxiliary features don't compete with the translator for attention.
  { path: '/crm',          tourKey: 'crmLeads',    labelKey: 'nav.crmLeads',    icon: PhoneIncoming, requires: 'crm', group: 'crm' },
  { path: '/crm/clients',  tourKey: 'crmClients',  labelKey: 'nav.crmClients',  icon: UserRound,     requires: 'crm', group: 'crm' },
  { path: '/crm/stats',    tourKey: 'crmStats',    labelKey: 'nav.crmStats',    icon: TrendingUp,    requires: 'crm', group: 'crm' },
  { path: '/outreach',     tourKey: 'outreach',    labelKey: 'nav.inbox',       icon: MessagesSquare, requires: 'outreach', group: 'conversations', badge: 'unread' },
  { path: '/outreach/mailboxes', tourKey: 'mailboxes', labelKey: 'nav.mailboxes', icon: Inbox, requires: 'outreach', group: 'conversations' },
  { path: '/outreach/leads',     tourKey: 'leads',     labelKey: 'nav.leads',     icon: Target, requires: 'outreach', group: 'conversations' },
  { path: '/outreach/contacts', tourKey: 'contacts', labelKey: 'nav.contacts', icon: Users, requires: 'outreach', group: 'conversations' },
  { path: '/outreach/campaigns', tourKey: 'campaigns', labelKey: 'nav.campaigns', icon: Megaphone, requires: 'outreach', group: 'conversations' },
  { path: '/outreach/analytics', tourKey: 'outreachAnalytics', labelKey: 'nav.outreachAnalytics', icon: BarChart3, requires: 'outreach', group: 'conversations' },
  { path: '/outreach/settings', tourKey: 'outreachSettings', labelKey: 'nav.outreachSettings', icon: MessagesSquare, requires: 'outreach', group: 'conversations' },
  { path: '/receipts',     tourKey: 'receipts',    labelKey: 'nav.receipts',    icon: Receipt,  group: 'others' },
];

// Group metadata. Every distinct `group` value in NAV_ITEMS gets one
// entry here providing the accordion header label + tour key; `parent`
// nests a group inside another. A group's own tourKey must differ from
// every child item's tourKey - both render a `data-tour` attribute and the
// tour matches on it.
export const NAV_GROUPS = {
  crm: { labelKey: 'nav.crm', tourKey: 'crmGroup', variant: 'primary', parent: 'others' },
  conversations: { labelKey: 'nav.outreach', tourKey: 'conversationsGroup', variant: 'primary', parent: 'others' },
  others: { labelKey: 'nav.others', tourKey: 'others' },
};

function groupChain(group) {
  const chain = [];
  for (let current = group; current; current = NAV_GROUPS[current].parent) chain.unshift(current);
  return chain;
}

// The sidebar's tree: items and groups in nav order, a group at its first
// member's place holding its own items and sub-groups. `items` on a group is
// every item under it, however deep.
export function navUnits(items, depth = 0) {
  const units = [];
  const seen = new Set();
  for (const item of items) {
    const group = item.group ? groupChain(item.group)[depth] : undefined;
    if (!group) {
      units.push({ kind: 'item', item });
      continue;
    }
    if (seen.has(group)) continue;
    seen.add(group);
    const members = items.filter((other) => other.group && groupChain(other.group)[depth] === group);
    units.push({ kind: 'group', group, items: members, units: navUnits(members, depth + 1) });
  }
  return units;
}
